import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { registrarDispositivo } from "../features/criador/dispositivo";
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, onSnapshot, collection, getDoc, query, orderBy, limit } from "firebase/firestore";
import Loading from '../components/layout/Loading';
import { callFirebaseFunction } from '../utils/firebaseFunctionCaller';
import { auth, db } from "../firebaseConfig";
import { Capacitor } from '@capacitor/core';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { logSessionStart } from '../services/analyticsService';

export const UserContext = createContext(null);
export const useUser = () => useContext(UserContext);

// Guard de sessão: backfill do recorde de ofensiva (XP_VITA.MELHOR_STREAK) roda
// no máximo 1x por uid (usuários antigos não têm o campo). Ver onSnapshot do Vita.
const _melhorStreakBackfilled = new Set();

// ── C6 (custo de leitura): janelas dos listeners de coleção do contexto ──────
// REFEICAO_ANALISE: consumidor mais fundo é o diário (HomePhotoNutrition, visão
// semanal) + quota scanfood (DIA_BRT, useEntitlements) + detecção de horário de
// refeição (useNotifications, precisa de ≥5 scans). 120 docs = ~20-40 dias até
// para usuário pesado (3-6 scans/dia). Períodos mais antigos são buscados sob
// demanda pelo próprio diário (fetchMealScansForPeriod).
export const MEAL_SCANS_WINDOW = 120;
// analises_fisicas: docs pesados (payload IA + PNG base64 legado). Consumidores
// de "última análise" (StepColeta, BoxTicket, ManualBioimpedanceForm) + quota
// scanbody (ROLLING_30D, máx. 3/30d + tickets) cabem folgados em 40. As telas de
// histórico completo (HistoricoAvaliacoes, avaliacao/index) usam useAllBodyScans,
// que busca o resto uma única vez SÓ se a janela estiver cheia.
export const BODY_SCANS_WINDOW = 40;

export const UserProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [user, setUser] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [patientDocId, setPatientDocId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calorieMacros, setCalorieMacros] = useState(null); 
  const [objectives, setObjectives] = useState([]);
  /* O OBJETIVO já foi lido ao menos uma vez nesta sessão?
     `objectives` nasce [] e o listener da subcoleção só é registrado DEPOIS
     do primeiro snapshot do doc PACIENTE — ou seja, existe uma janela em que
     o perfil já chegou e o objetivo ainda não. Quem decide entre "mostrar o
     onboarding" e "abrir o app" olhava só para `objectives.length` e, nessa
     janela, via zero: era isso que fazia a tela de objetivo piscar na frente
     de quem já a tinha preenchido. */
  const [objectivesLoaded, setObjectivesLoaded] = useState(false);
  const [bodyScans, setBodyScans] = useState([]);
  const [mealScans, setMealScans] = useState([]);
  const [nutritionalPlans, setNutritionalPlans] = useState([]);
  const [vitaData, setVitaData] = useState(null);
  const [areaScores, setAreaScores] = useState({});

  const isNative = Capacitor.isNativePlatform();
  const refreshUserData = useCallback(async () => {
    try {
      const data = await callFirebaseFunction('getUserDashboardData');
      if (data.success) {
        setUser(data.userProfile);
        setProfileData(data.patientProfile);
        setObjectives(data.objectives);
        setObjectivesLoaded(true);
        // A callable devolve os docs sem `id` até a versão que o inclui estar
        // no ar. Enquanto isso, o id vem do que o listener já tinha: mesmo
        // `ordem_analise` e mesma data é o mesmo doc.
        setBodyScans((anteriores) => (data.bodyScans || []).map((scan) => {
          if (scan?.id) return scan;
          const seg = (v) => v?.seconds ?? v?._seconds ?? null;
          const par = (anteriores || []).find((a) => a?.id
            && a.info_analise?.ordem_analise === scan?.info_analise?.ordem_analise
            && seg(a.info_analise?.data) === seg(scan?.info_analise?.data));
          return par ? { id: par.id, ...scan } : scan;
        }));
      }
    } catch (error) {
      console.error("[UserContext] Erro ao atualizar dados manualmente:", error);
    }
  }, []); 

const identifyRevenueCatUser = async (uid) => {
    if (!isNative || !uid) return;
    try {
      await Purchases.logIn({ appUserID: uid });
    } catch (e) {
      console.warn("[RC] Falha na identificação do usuário:", e); 
    }
  };


  useEffect(() => {
    let firestoreUnsubscribers = [];

    const unsubscribeAuth = onAuthStateChanged(auth, async (authenticatedUser) => {

      firestoreUnsubscribers.forEach(unsub => unsub());
      firestoreUnsubscribers = []; 

      if (authenticatedUser) {
        setCurrentUser(authenticatedUser);
        const userId = authenticatedUser.uid;
        await identifyRevenueCatUser(userId);
        logSessionStart();
        // Antifraude do Programa Criadores Parceiros: aparelho da sessão
        // (1x por login; best-effort, nunca atrasa nem derruba o login).
        registrarDispositivo(authenticatedUser);

        // Listener para USER
        const userRef = doc(db, 'USER', userId);
        firestoreUnsubscribers.push(onSnapshot(userRef, (userDoc) => {
          setUser(userDoc.exists() ? { id: userDoc.id, ...userDoc.data() } : null);
        }));

        // Listener para PACIENTE e suas subcoleções.
        // C6 (leak de listeners): os listeners aninhados abaixo dependem SÓ de
        // userId (nenhum dado do doc PACIENTE) — só precisam esperar o doc
        // existir. Sem o guard, CADA update do doc PACIENTE re-registrava os
        // ~10 listeners sem cancelar os anteriores (N cópias vivas → N× leituras
        // de cada coleção e N×3 getDoc no callback do PLANO_NUTRICIONAL).
        // Agora registram UMA vez por sessão de auth, no 1º snapshot com o doc
        // existente (mesma condição de antes). Cancelamento continua no
        // auth-change/unmount via firestoreUnsubscribers (inalterado).
        let patientSubsRegistered = false;
        const patientRef = doc(db, 'PACIENTE', userId);
        firestoreUnsubscribers.push(onSnapshot(patientRef, (patientDoc) => {
          if (patientDoc.exists()) {
            setProfileData({ id: patientDoc.id, ...patientDoc.data() });
            setPatientDocId(patientDoc.id);

            if (patientSubsRegistered) return;
            patientSubsRegistered = true;

            const vitaRef = doc(db, 'PACIENTE', userId, 'VITA', 'ROOT');
            firestoreUnsubscribers.push(onSnapshot(vitaRef, (snap) => {
              const vd = snap.exists() ? snap.data() : {};
              setVitaData(vd);

              // Backfill ÚNICO do recorde de ofensiva: contas antigas não têm
              // XP_VITA.MELHOR_STREAK → recalcula do histórico 1x (a função grava
              // o campo, então os próximos snapshots já vêm preenchidos).
              if (
                vd?.XP_VITA &&
                vd.XP_VITA.MELHOR_STREAK === undefined &&
                !_melhorStreakBackfilled.has(userId)
              ) {
                _melhorStreakBackfilled.add(userId);
                callFirebaseFunction("recalculateStreakFromHistory", {}).catch(() => {
                  _melhorStreakBackfilled.delete(userId); // permite retry se falhar
                });
              }
            }));

            const areaTreino = doc(db, 'PACIENTE', userId, 'VITA', 'ROOT', 'AREAS', 'TREINO');
            const areaDieta  = doc(db, 'PACIENTE', userId, 'VITA', 'ROOT', 'AREAS', 'DIETA');
            const areaCorpo  = doc(db, 'PACIENTE', userId, 'VITA', 'ROOT', 'AREAS', 'CORPO');

            firestoreUnsubscribers.push(onSnapshot(areaTreino, (snap) => {
              setAreaScores(prev => ({ ...prev, TREINO: snap.exists() ? snap.data() : null }));
            }));
            firestoreUnsubscribers.push(onSnapshot(areaDieta, (snap) => {
              setAreaScores(prev => ({ ...prev, DIETA: snap.exists() ? snap.data() : null }));
            }));
            firestoreUnsubscribers.push(onSnapshot(areaCorpo, (snap) => {
              setAreaScores(prev => ({ ...prev, CORPO: snap.exists() ? snap.data() : null }));
            }));

            // Listeners para subcoleções
            const objRef = collection(db, 'PACIENTE', userId, 'OBJETIVO');
            firestoreUnsubscribers.push(onSnapshot(
              objRef,
              (snap) => {
                setObjectives(snap.docs.map(d => ({ id: d.id, ...d.data() })));
                setObjectivesLoaded(true);
              },
              // Falha de leitura também encerra a espera: sem isto, um erro de
              // permissão deixaria o app parado no Loading para sempre.
              () => setObjectivesLoaded(true),
            ));

            // C6: janela nas coleções sem teto (ver constantes no topo do arquivo).
            // Ordenação espelha a que os consumidores já usavam em JS.
            const bsRef = query(
              collection(db, 'PACIENTE', userId, 'analises_fisicas'),
              orderBy('info_analise.ordem_analise', 'desc'),
              limit(BODY_SCANS_WINDOW)
            );
            // `id` junto (como OBJETIVO e REFEICAO_ANALISE já fazem): a Análise
            // Corporal abre um doc pelo id, porque `ordem_analise` repete.
            firestoreUnsubscribers.push(onSnapshot(bsRef, snap => setBodyScans(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => b.info_analise.ordem_analise - a.info_analise.ordem_analise))));

            const msRef = query(
              collection(db, 'PACIENTE', userId, 'REFEICAO_ANALISE'),
              orderBy('createdAt', 'desc'),
              limit(MEAL_SCANS_WINDOW)
            );
            firestoreUnsubscribers.push(onSnapshot(msRef, snap => setMealScans(snap.docs.map(d => ({ id: d.id, ...d.data() })))));

            const dietRef = doc(db, 'PACIENTE', userId, 'PLANO_DIETA', 'CALORIA_MACROS');
            firestoreUnsubscribers.push(onSnapshot(dietRef, dietDoc => setCalorieMacros(dietDoc.exists() ? dietDoc.data() : null)));

            const plansRef = collection(db, 'PACIENTE', userId, 'PLANO_NUTRICIONAL');
            /* CORRIDA ENTRE SNAPSHOTS (07/10/2026, "gerou a dieta mas o treino
               não"): a geração grava o CURRENT em 3 tempos (Python com a dieta,
               o `set` de garantia do Node, o ponteiro do treino 0,6 s depois).
               Cada escrita dispara este callback, que é async e busca os
               subdocs — e os callbacks se SOBREPÕEM. Se o último (o que tem o
               treino) falhava numa leitura ou chegava antes de um anterior, o
               anterior (sem treino) ficava por último e o treino sumia até
               reabrir o app. Agora: só o resultado do snapshot MAIS RECENTE é
               aplicado, e falha de leitura tenta de novo em vez de zerar. */
            let seqPlano = 0;
            const processarPlanos = async (snap, minhaVez, tentativa = 1) => {
                try {
                  const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));

                  // Helper seguro para timestamps (legado e novo)
                  const getTs = (item) => {
                    if (!item || typeof item !== "object") return 0;

                    return (
                      item?.DATA_CREATED?.seconds ||
                      item?.DATA_CREATED?._seconds ||
                      item?.UPDATED_AT?.seconds ||
                      item?.UPDATED_AT?._seconds ||
                      item?.CREATED_AT?.seconds ||
                      item?.CREATED_AT?._seconds ||
                      0
                    );
                  };

                  // 1) Tenta nova estrutura (doc CURRENT + latest refs)
                  const currentRoot = docs.find((d) => d?.id === "CURRENT" || d?.DOC_TYPE === "PLANO_NUTRICIONAL_ROOT");

                  if (currentRoot?.LATEST_REFS) {
                    const latest = { ...(currentRoot.LATEST_REFS || {}) };
                    // Ponteiro do treino vazio com um treino ativo marcado: usa o ativo.
                    if (!latest.TREINO && currentRoot.TREINO_ATIVO_ID) {
                      latest.TREINO = currentRoot.TREINO_ATIVO_ID;
                    }

                    const [planoMod, dietaMod, treinoMod] = await Promise.all([
                      latest.PLANO
                        ? getDoc(doc(db, 'PACIENTE', userId, 'PLANO_NUTRICIONAL', 'CURRENT', 'PLANO', latest.PLANO))
                        : Promise.resolve(null),

                      latest.DIETA
                        ? getDoc(doc(db, 'PACIENTE', userId, 'PLANO_NUTRICIONAL', 'CURRENT', 'DIETA', latest.DIETA))
                        : Promise.resolve(null),

                      latest.TREINO
                        ? getDoc(doc(db, 'PACIENTE', userId, 'PLANO_NUTRICIONAL', 'CURRENT', 'TREINO', latest.TREINO))
                        : Promise.resolve(null),
                    ]);

                    const planoData = planoMod?.exists?.() ? planoMod.data() : null;
                    const dietaData = dietaMod?.exists?.() ? dietaMod.data() : null;
                    const treinoData = treinoMod?.exists?.() ? treinoMod.data() : null;

                    // [DIETA_DEBUG] — remover após confirmar formato em produção
                    // if (latest.DIETA) {
                    //   console.log('[DIETA_DEBUG] id buscado:', latest.DIETA);
                    //   console.log('[DIETA_DEBUG] doc exists:', dietaMod?.exists?.());
                    //   console.log('[DIETA_DEBUG] chaves do doc:', dietaData ? Object.keys(dietaData) : 'null');
                    // }

                    // Tolerância de formato:
                    //   Formato A (backend Python): doc = { DIETA: { PLANO_DIETA, ... } }
                    //   Formato B (painel nutri):   doc = { PLANO_DIETA, ... } (conteúdo na raiz)
                    const dietaContent = (dietaData?.DIETA && dietaData.DIETA.PLANO_DIETA)
                      ? dietaData.DIETA
                      : (dietaData?.PLANO_DIETA ? dietaData : null);

                    // [DIETA_DEBUG] parte 2 — remover após confirmar formato em produção
                    // if (latest.DIETA) {
                    //   console.log('[DIETA_DEBUG] dietaContent tem PLANO_DIETA?', !!dietaContent?.PLANO_DIETA, '| keys:', dietaContent ? Object.keys(dietaContent) : 'null');
                    //   console.log('[DIETA_DEBUG] planoNormalizado.DIETA setado?', !!dietaContent);
                    // }

                    // Normaliza para formato antigo esperado pelo front
                    const planoNormalizado = {
                      id: "CURRENT_NORMALIZED",
                      ALVO: planoData?.ALVO || null,
                      PLANO: planoData?.PLANO || null,
                      DIETA: dietaContent,
                      TREINO: treinoData?.TREINO || null,
                      DATA_CREATED: currentRoot?.UPDATED_AT || currentRoot?.CREATED_AT || null,
                      __SOURCE: "NEW_STRUCTURE",
                    };

                    if (minhaVez !== seqPlano) return; // chegou um snapshot mais novo
                    setNutritionalPlans([planoNormalizado]);
                    return;
                  }
                  
                  // 2) Fallback legado (ignora docs sem shape antigo)
                  const legacyPlans = docs
                    .filter((p) => p && (p.ALVO || p.PLANO || p.DIETA || p.TREINO))
                    .sort((a, b) => getTs(b) - getTs(a));

                  if (minhaVez !== seqPlano) return;
                  setNutritionalPlans(legacyPlans);
                } catch (err) {
                  console.error("Erro ao processar PLANO_NUTRICIONAL:", err);
                  // Não zera o plano que já está na tela: tenta de novo (leitura
                  // de subdoc fora do cache falha com o app sem rede por um
                  // instante). Só o snapshot mais recente tenta.
                  if (minhaVez === seqPlano && tentativa < 4) {
                    setTimeout(() => {
                      if (minhaVez === seqPlano) processarPlanos(snap, minhaVez, tentativa + 1);
                    }, 1500 * tentativa);
                  }
                }
            };
            firestoreUnsubscribers.push(
              onSnapshot(plansRef, (snap) => {
                seqPlano += 1;
                processarPlanos(snap, seqPlano);
              })
            );
          } else {
            setProfileData(null);
            setPatientDocId(null);
            // Sem doc PACIENTE não há subcoleção OBJETIVO para esperar.
            setObjectivesLoaded(true);
            
          }
        }));
        setLoading(false);
      } else {
        setCurrentUser(null);
        setUser(null);
        setProfileData(null);
        setPatientDocId(null);
        setObjectives([]);
        setObjectivesLoaded(false);
        setBodyScans([]);
        setMealScans([]);
        setCalorieMacros(null);
        setVitaData(null);
        setLoading(false);
        setNutritionalPlans([]); 
        setAreaScores({});
      }
    });

    return () => {
      unsubscribeAuth();
      firestoreUnsubscribers.forEach(unsub => unsub());
    };

  }, [auth, db, isNative]); 

  const logout = async () => {
    try {
      await signOut(auth);
      if (isNative) {
        try {
          await Purchases.logOut();
        } catch (e) {
          console.warn("[RevenueCat] Erro ao fazer logOut:", e);
        }
      }
    } catch (error) {
      console.error("Erro ao sair:", error);
      throw error;
    }
  };


  const value = {
    currentUser,
    user, 
    profileData, 
    patientDocId, 
    loading, 
    logout,
    objectives, 
    objectivesLoaded,
    bodyScans, 
    mealScans, 
    calorieMacros,
    nutritionalPlans,
    refreshUserData,
    vitaData,
    areaScores,
  };

  if (loading) return <Loading />;

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};